import { useState, useEffect } from 'react';
import useTranslations from '~/components/translation/use-translations';
import insertValue from '~/components/casino-shared/tournaments/utils/insert-value';

// time left text is disabled due to compliance issue
const HOURS_THRESHOLD = -1;

const TEXT_KEYS = [
	'TOURNAMENTS_TIME_ACTIVE',
	'TOURNAMENTS_TIME_ENDED',
	'TOURNAMENTS_TIME_LEFT_D',
	'TOURNAMENTS_TIME_LEFT_H',
	'TOURNAMENTS_TIME_LEFT_M',
	'TOURNAMENTS_TIME_UNTIL_D',
	'TOURNAMENTS_TIME_UNTIL_H',
	'TOURNAMENTS_TIME_UNTIL_M'
];

const getTimeText = ({
	hasStarted,
	hasEnded,
	timeLeft,
	translations,
	isLoadingTranslations
}: {
	hasStarted: boolean;
	hasEnded: boolean;
	timeLeft: number;
	translations: Record<string, string>;
	isLoadingTranslations: boolean;
}) => {
	if (isLoadingTranslations) {
		return '';
	}

	if (hasEnded) {
		return translations.TOURNAMENTS_TIME_ENDED;
	}

	const minsLeft = timeLeft / 60 / 1000;
	const hoursLeft = minsLeft / 60;
	const daysLeft = hoursLeft / 24;

	if (hasStarted) {
		if (hoursLeft > HOURS_THRESHOLD) {
			return translations.TOURNAMENTS_TIME_ACTIVE;
		}
		if (daysLeft > 1) {
			return insertValue(translations.TOURNAMENTS_TIME_LEFT_D, daysLeft);
		}
		if (hoursLeft > 1) {
			return insertValue(translations.TOURNAMENTS_TIME_LEFT_H, hoursLeft);
		}
		if (minsLeft > 1) {
			return insertValue(translations.TOURNAMENTS_TIME_LEFT_M, minsLeft);
		}
		return insertValue(translations.TOURNAMENTS_TIME_LEFT_M, 1);
	}

	if (daysLeft > 1) {
		return insertValue(translations.TOURNAMENTS_TIME_UNTIL_D, daysLeft);
	}
	if (hoursLeft > 1) {
		return insertValue(translations.TOURNAMENTS_TIME_UNTIL_H, hoursLeft);
	}
	if (minsLeft > 1) {
		return insertValue(translations.TOURNAMENTS_TIME_UNTIL_M, minsLeft);
	}
	return insertValue(translations.TOURNAMENTS_TIME_UNTIL_M, 1);
};

const useTimeTicker = (startDate: string, endDate: string) => {
	const [hasStarted, setHasStarted] = useState(false);
	const [hasEnded, setHasEnded] = useState(false);
	const [timeText, setTimeText] = useState('');
	const { translations, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	useEffect(() => {
		const startTime = new Date(startDate).getTime();
		const endTime = new Date(endDate).getTime();
		let timeLeft = 0;
		let requestId = 0;
		let isMounted = true;

		const tick = () => {
			const now = Date.now();
			const started = now > startTime;
			const ended = now > endTime;

			timeLeft = (started ? endTime : startTime) - now;

			setHasStarted(started);
			setHasEnded(ended);
			setTimeText(
				getTimeText({
					hasStarted: started,
					hasEnded: ended,
					timeLeft,
					translations,
					isLoadingTranslations
				})
			);

			if (!ended && timeLeft > 0) {
				setTimeout(() => {
					if (isMounted) {
						requestId = window.requestAnimationFrame(tick);
					}
				}, 1000);
			}
		};

		requestId = window.requestAnimationFrame(tick);

		return () => {
			window.cancelAnimationFrame(requestId);
			isMounted = false;
		};
	}, [startDate, endDate, translations, isLoadingTranslations]);

	return {
		hasStarted,
		hasEnded,
		timeText
	};
};

export default useTimeTicker;
